import { useState, useEffect, useRef, useCallback } from "react";
import { Transaction } from "@/types/transaction";
import { fetchTransactions } from "@/services/transactions/transactionFetchService";
import { fetchStripeAccountId } from "@/services/transactions/stripeAccountService";

const CACHE_KEY = 'keepmore_transactions_cache';
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

interface CacheData {
  transactions: Transaction[];
  timestamp: number;
}

interface UseTransactionsFetcherOptions {
  limit?: number;
  initialStatus?: string;
}

// Module-level state to persist across component unmounts/remounts.
// This prevents re-fetching when switching tabs — data stays in memory
// without needing a React Context (which causes auth race conditions).
const moduleState = {
  hasInitialized: false,
  hasRunBackgroundFetch: false,
  transactions: [] as Transaction[],
  accountId: null as string | null,
  hasMore: false,
  nextCursor: null as string | null,
  hasTransactions: false,
};

export function useTransactionsFetcher({
  limit = 100,
  initialStatus,
}: UseTransactionsFetcherOptions = {}) {
  // Initialize state from module-level cache if available
  const [transactions, setTransactions] = useState<Transaction[]>(moduleState.transactions);
  const [loading, setLoading] = useState(!moduleState.hasInitialized);
  const [isFetchingFresh, setIsFetchingFresh] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(moduleState.hasMore);
  const [nextCursor, setNextCursor] = useState<string | null>(moduleState.nextCursor);
  const [accountId, setAccountId] = useState<string | null>(moduleState.accountId);
  const [hasTransactions, setHasTransactions] = useState(moduleState.hasTransactions);
  const [currentStatus, setCurrentStatus] = useState<string | undefined>(
    initialStatus ?? "all"
  );

  const mountedRef = useRef(true);
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef<number>(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // This effect only syncs the status filter — does NOT trigger new fetches.
  // Fetches are only triggered by: initial load OR manual refresh.
  useEffect(() => {
    if (initialStatus !== currentStatus && moduleState.hasInitialized) {
      setCurrentStatus(initialStatus ?? "all");
    }
  }, [initialStatus, currentStatus]);

  const loadCachedData = useCallback(() => {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const { transactions: cachedTransactions, timestamp }: CacheData =
          JSON.parse(cached);
        const age = Date.now() - timestamp;

        if (age < CACHE_EXPIRY) {
          setTransactions(cachedTransactions);
          if (cachedTransactions.length > 0) setHasTransactions(true);
        } else {
          localStorage.removeItem(CACHE_KEY);
        }
        setLoading(false);
        return age < CACHE_EXPIRY;
      } catch {
        localStorage.removeItem(CACHE_KEY);
        setLoading(false);
      }
    }
    return false;
  }, []);

  const cacheTransactions = useCallback(
    (transactionsToCache: Transaction[]) => {
      if (!mountedRef.current) return;
      const cacheData: CacheData = {
        transactions: transactionsToCache,
        timestamp: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      if (transactionsToCache.length > 0) setHasTransactions(true);
    },
    []
  );

  const loadTransactions = useCallback(
    async (cursor?: string, idOverride?: string, statusOverride?: string) => {
      const currentId = idOverride || accountId;
      const status = statusOverride ?? currentStatus ?? "all";

      if (!currentId) {
        setLoading(false);
        isFetchingRef.current = false;
        return;
      }

      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      setIsFetchingFresh(true);
      lastFetchTimeRef.current = Date.now();

      try {
        setError(null);
        const response = await fetchTransactions(
          currentId,
          limit,
          status,
          cursor
        );
        if (!mountedRef.current || !response) return;

        const newTransactions = cursor
          ? [...transactions, ...response.transactions]
          : response.transactions;

        setTransactions(newTransactions);
        setHasMore(response.has_more);
        setNextCursor(response.next_cursor);
        cacheTransactions(newTransactions);
        if (newTransactions.length > 0) setHasTransactions(true);

        // Update module-level state for persistence across remounts
        moduleState.transactions = newTransactions;
        moduleState.hasMore = response.has_more;
        moduleState.nextCursor = response.next_cursor;
        moduleState.hasTransactions = newTransactions.length > 0;
      } catch (err: unknown) {
        if (mountedRef.current)
          setError((err as Error).message || "Failed to fetch transactions");
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setIsFetchingFresh(false);
          isFetchingRef.current = false;
        }
      }
    },
    [accountId, cacheTransactions, currentStatus, limit, transactions]
  );

  const loadMore = useCallback(() => {
    if (nextCursor && !loading && !isFetchingRef.current) {
      loadTransactions(nextCursor);
    }
  }, [nextCursor, loading, loadTransactions]);

  // Force refresh function — called by refresh button
  const forceRefresh = useCallback(() => {
    if (accountId && !isFetchingRef.current) {
      console.log('[TransactionsFetcher] Force refresh triggered');
      lastFetchTimeRef.current = Date.now();
      return loadTransactions(undefined, accountId, currentStatus);
    }
  }, [accountId, currentStatus, loadTransactions]);

  // One-time initialization using module-level guard
  useEffect(() => {
    if (moduleState.hasInitialized) return;
    moduleState.hasInitialized = true;

    loadCachedData();

    const initialize = async () => {
      try {
        const id = await fetchStripeAccountId();

        if (!mountedRef.current) return;

        setAccountId(id);
        moduleState.accountId = id;

        if (!id) {
          setLoading(false);
          isFetchingRef.current = false;
          return;
        }

        if (!moduleState.hasRunBackgroundFetch) {
          moduleState.hasRunBackgroundFetch = true;
          await loadTransactions(undefined, id, currentStatus);
        }
      } catch {
        if (mountedRef.current) {
          setLoading(false);
          isFetchingRef.current = false;
        }
      }
    };

    initialize();
  }, [loadCachedData, loadTransactions, currentStatus]);

  // Listen for custom events (refresh button, stripe-connected, etc.)
  useEffect(() => {
    const handleTransactionRefresh = () => {
      if (accountId && !isFetchingRef.current) {
        loadTransactions(undefined, accountId, currentStatus);
      }
    };

    const handleForceRefresh = () => {
      console.log('[TransactionsFetcher] Force refresh event received');
      if (accountId && !isFetchingRef.current) {
        lastFetchTimeRef.current = Date.now();
        loadTransactions(undefined, accountId, currentStatus);
      }
    };

    document.addEventListener("transaction-refresh", handleTransactionRefresh);
    document.addEventListener("transaction-force-refresh", handleForceRefresh);

    return () => {
      document.removeEventListener(
        "transaction-refresh",
        handleTransactionRefresh
      );
      document.removeEventListener(
        "transaction-force-refresh",
        handleForceRefresh
      );
    };
  }, [accountId, currentStatus, loadTransactions]);

  return {
    transactions,
    loading,
    isFetchingFresh,
    error,
    hasMore,
    nextCursor,
    accountId,
    hasStripeAccount: !!accountId,
    hasTransactions,
    loadTransactions,
    loadMore,
    forceRefresh,
    currentStatus,
    setCurrentStatus: (status: string | undefined) => {
      setCurrentStatus(status);
      // Note: We no longer auto-fetch when status changes.
      // Users should use the refresh button if they want fresh data.
    },
  };
}
